/**
 * Pure, React-free economics derivations for the Analytics tab.
 *
 * This is the single source of the tab's math (the v1.1 "one helper so figures
 * can't drift" lesson). Plan 02 renders these contracts without re-deriving
 * anything. All money stays as backend Decimal STRINGS (passed through or via
 * sumDecimalStrings); only day-counts and percentages are numbers. No
 * parseFloat / Number() ever touches an authoritative money value (HON-03).
 *
 * Lifecycle timing is anchored on the SETTLEMENT (active) date per Decision #2,
 * never trade_date. Date math parses YYYY-MM-DD as UTC midnight and compares in
 * whole days to avoid local-timezone off-by-one near midnight.
 */

import { formatDecimalParts, sumDecimalStrings, toDecimalString } from '@/utils/decimal-display';
import type { AccrualEntry } from '@/types';
import type { PositionDetail } from '@/types/trades.types';

/**
 * Parse a date to a UTC-midnight epoch (ms). Accepts a bare `YYYY-MM-DD` AND a
 * full ISO datetime (`YYYY-MM-DDTHH:mm:ssZ`) by matching only the date prefix.
 * settlement_date / maturity_date / actual_settlement_date are bare YYYY-MM-DD
 * (DRF DateField); only trade_date carries a `T..Z` time (DateTimeField). The
 * prefix-match parse handles both, so a future change in any field's granularity
 * can't reintroduce the old `$`-anchored-regex bug (it rejected `T..Z` → NaN →
 * "0 days active" + a never-rendering term bar, QA findings B & C). Coerces type
 * defensively (a date field can arrive non-string at runtime).
 */
function parseUtcMidnight(dateStr: string | number | null | undefined): number {
  if (dateStr == null) return NaN;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr).trim());
  if (!match) return NaN;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return Date.UTC(year, month - 1, day);
}

/** Normalise an arbitrary Date to its UTC-midnight epoch (ms). */
function toUtcMidnight(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * True when a Decimal value is null/empty or numerically zero. Accepts a number
 * too — the live backend can send a JS number for a string-typed DecimalField,
 * and a raw `.trim()` on a number crashes the tab. toDecimalString coerces with
 * NO float arithmetic (HON-03 holds).
 */
export function isNullOrZeroDecimal(value: string | number | null | undefined): boolean {
  const str = toDecimalString(value);
  if (str == null) return true;
  // Zero iff every digit is 0 (ignore sign, point). Pure string check.
  return /^[+-]?0*(\.0*)?$/.test(str);
}

/**
 * LR-459: a cross-currency trade carries a settlement-currency twin of the daily
 * return (`daily_earnings_converted`) to display alongside the security-currency
 * figure. True iff the backend sent a WELL-FORMED converted value AND the
 * settlement currency genuinely differs from the security currency. Uses
 * formatDecimalParts (not just `!= null`) so a malformed/garbage string is
 * rejected rather than rendered as "≈ —". Single source so KpiStrip /
 * StickySummary / PnlTable can't drift (the mirror invariant).
 */
export function hasSettlementDailyReturn(
  trade: Pick<PositionDetail, 'daily_earnings_converted' | 'settlement_currency' | 'currency'>,
): boolean {
  return (
    !formatDecimalParts(trade.daily_earnings_converted).isEmpty &&
    !!trade.settlement_currency &&
    // Case-insensitive (LR-474 r1): mirrors settlement-ccy.ts isCrossCurrency so
    // a non-canonical 'usd' vs 'USD' payload classifies the same way here as in
    // the blotter helpers.
    trade.settlement_currency.toUpperCase() !== (trade.currency || '').toUpperCase()
  );
}

/**
 * LR-749: a position's accrued total per currency its AccrualEntry rows are in.
 * The backend converts a cross-currency GMSLA trade's rows to the settlement
 * currency and stamps each with the `fx_rate` it used; an unstamped row is in the
 * security currency. Usually one entry. Two when a statement issued or paid
 * before the conversion kept its rows in the security currency (its total is a
 * figure of record) and later rows are converted: the page has no FX to
 * reconcile those, so they are never summed together. Settlement currency first,
 * as the page leads with it; empty when there are no rows.
 */
export function accruedByCurrency(
  trade: Pick<PositionDetail, 'currency' | 'settlement_currency'>,
  accruals: AccrualEntry[] | undefined,
): { currency: string; net: string }[] {
  const rows = accruals ?? [];
  const groups: [string, AccrualEntry[]][] = [
    [trade.settlement_currency || trade.currency, rows.filter((a) => a.fx_rate != null)],
    [trade.currency, rows.filter((a) => a.fx_rate == null)],
  ];
  return groups
    .filter(([, group]) => group.length > 0)
    .map(([currency, group]) => ({ currency, net: deriveAccruedToDate(group).net }));
}

/**
 * LR-749: the currency to label anything summed from a position's AccrualEntry
 * rows with; `null` when they are in two (see `accruedByCurrency`), since then no
 * single sum means anything.
 */
export function accrualCurrency(
  trade: Pick<PositionDetail, 'currency' | 'settlement_currency'>,
  accruals: AccrualEntry[] | undefined,
): string | null {
  const groups = accruedByCurrency(trade, accruals);
  return groups.length > 1 ? null : (groups[0]?.currency ?? trade.currency);
}

/**
 * Sum of all real AccrualEntry.net_amount rows for a position, the row count, and
 * the number of DISTINCT accrual dates. The hero figure (ANLY-01). Decimal-safe
 * via sumDecimalStrings.
 *
 * `days` is the single day-basis for the card (LR-414 B): it counts distinct
 * `accrual_date`s, which is exactly what AccrualChart groups its bars by, so the
 * KPI "Accrued · N days" can't disagree with the chart's bar count. It differs
 * from `count` (raw row count) only for FOP fee-mode, where a day carries two
 * legs (fee + rebate) → two rows on one date. Rows without an `accrual_date` are
 * ignored (mirrors AccrualChart's `if (!a.accrual_date) continue`).
 */
export function deriveAccruedToDate(accruals: AccrualEntry[]): {
  net: string;
  count: number;
  days: number;
} {
  const distinctDates = new Set<string>();
  for (const a of accruals) {
    if (a.accrual_date) distinctDates.add(a.accrual_date);
  }
  return {
    net: sumDecimalStrings(accruals.map((a) => a.net_amount)),
    count: accruals.length,
    days: distinctDates.size,
  };
}

/**
 * The set of `Position.status` values that mean "this trade is done — stop
 * counting live earnings, hide projections, freeze day counters".
 *
 * Single source of truth (LR-208 r5 M3): pre-r5 the same `closed || cancelled
 * || matured` triple was duplicated inline in KpiStrip / StickySummary /
 * PnlTable / RateWaterfall / AnalyticsTab / TradeTermsTab — six copies with
 * comments referring to one another. A future expansion (e.g. LR-408
 * promoting `'rejected'` to terminal, or Phase-2 PositionEvent adding a new
 * end state) would require six synchronous edits with no test to catch a
 * desync. Now they all import this predicate.
 */
export const TERMINAL_STATUSES = ['closed', 'cancelled', 'matured'] as const;

export function isTerminalStatus(status: PositionDetail['status']): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

export type TermProgress =
  | { hasTerm: false }
  | { hasTerm: true; percentElapsed: number };

/**
 * Term-elapsed progress for the lifecycle-timing card (ANLY-05).
 *
 * - 'term' with a maturity_date  -> hasTerm true, percentElapsed clamped 0..100
 *   = (today − settlement) / (maturity − settlement) * 100.
 * - 'open' (or any type without a maturity_date) -> hasTerm false (UI hides bar).
 * - 'evergreen' -> hasTerm true ONLY if notice_served AND maturity_date present;
 *   otherwise treated as open (UI-SPEC §④).
 *
 * All date math anchors on settlement_date.
 */
export function deriveTermProgress(
  settlementDate: string,
  maturityDate: string | null,
  termType: PositionDetail['term_type'],
  noticeServed: boolean,
  today: Date = new Date(),
): TermProgress {
  if (!maturityDate) return { hasTerm: false };

  if (termType === 'open') return { hasTerm: false };
  if (termType === 'evergreen' && !noticeServed) return { hasTerm: false };

  const settlementMs = parseUtcMidnight(settlementDate);
  const maturityMs = parseUtcMidnight(maturityDate);
  const todayMs = toUtcMidnight(today);

  if (Number.isNaN(settlementMs) || Number.isNaN(maturityMs)) {
    return { hasTerm: false };
  }

  const totalSpan = maturityMs - settlementMs;
  if (totalSpan <= 0) {
    // Degenerate / same-day or inverted term — no meaningful progress bar.
    return { hasTerm: false };
  }

  const elapsed = todayMs - settlementMs;
  const rawPct = (elapsed / totalSpan) * 100;
  const percentElapsed = Math.max(0, Math.min(100, Math.round(rawPct)));

  return { hasTerm: true, percentElapsed };
}

/**
 * Presence flags for the income-decomposition strip (ANLY-03 honest-empty
 * rule). A flag is true only when the field is non-null AND not a zero Decimal
 * string ("0" / "0.00..."), so the UI renders "—" or omits a tile rather than
 * "$0.00" fiction.
 *
 * `interest_amount` is the GMRA leg — it may be present on the position payload
 * even though the strict PositionDetail TS type omits it, so it is read
 * structurally.
 */
export function deriveDecompositionPresence(
  position: Pick<
    PositionDetail, 'fee_income' | 'cost_of_collateral' | 'effective_cost_of_collateral'
  > & {
    interest_amount?: string | null;
  },
): { hasFee: boolean; hasCost: boolean; hasInterest: boolean } {
  return {
    hasFee: !isNullOrZeroDecimal(position.fee_income),
    // LR-631: the gate reads the SAME figure the row renders — the effective
    // cost when the pool serves one. Gating on the booked twin would drop the
    // Cost row on a pooled fee trade whose booking snapshot happens to be zero,
    // while the Net beside it still carried that cost.
    hasCost: !isNullOrZeroDecimal(
      position.effective_cost_of_collateral ?? position.cost_of_collateral,
    ),
    hasInterest: !isNullOrZeroDecimal(position.interest_amount),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Margin & collateral adequacy (ANLY-04, Decision #5)
//
// Extracted here so the Margin card AND the page KPI bar read ONE source — the
// v1.1 "one helper so figures can't drift" lesson. All math is Decimal-string /
// BigInt minor-unit (no parseFloat on authoritative money — HON-03).
// ───────────────────────────────────────────────────────────────────────────

/** Flip the sign of a Decimal value (pure string). Empty/absent → "0". */
function negateDecimalString(value: string | number | null | undefined): string {
  const t = toDecimalString(value);
  if (t == null) return '0';
  if (t.startsWith('-')) return t.slice(1);
  if (t.startsWith('+')) return `-${t.slice(1)}`;
  return `-${t}`;
}

/** Decimal-safe (a − b) without ever passing an authoritative value through parseFloat. */
function subtractDecimalStrings(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): string {
  const aStr = toDecimalString(a);
  const bStr = toDecimalString(b);
  const negB = bStr == null ? null : negateDecimalString(bStr);
  return sumDecimalStrings([aStr ?? '0', negB]);
}

/** Decimal-safe price × integer quantity at 8dp BigInt. Null if price absent/malformed. */
function multiplyPriceByQuantity(
  price: string | number | null | undefined,
  quantity: number,
): string | null {
  const priceStr = toDecimalString(price);
  if (priceStr == null || !Number.isInteger(quantity)) return null;
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(priceStr);
  if (!match) return null;
  const negative = match[1] === '-';
  const intPart = match[2] ?? '';
  const fracRaw = match[3] ?? '';
  // The regex also matches a lone '.', '+', '-' or '' (no digits on either side
  // of the point) → would silently compute 0 exposure rather than "no mark".
  // Reject when there is no digit at all (LR-208 r6 LOW).
  if (intPart === '' && fracRaw === '') return null;
  const fracPart = fracRaw.slice(0, 8).padEnd(8, '0');
  const digits = (intPart === '' ? '0' : intPart) + fracPart;
  let minor: bigint;
  try {
    minor = BigInt(digits) * BigInt(Math.abs(quantity));
  } catch {
    return null;
  }
  if (negative) minor = -minor;
  const abs = (minor < 0n ? -minor : minor).toString().padStart(9, '0');
  const sign = minor < 0n && !/^0*$/.test(abs) ? '-' : '';
  return `${sign}${abs.slice(0, abs.length - 8)}.${abs.slice(abs.length - 8)}`;
}

export type MarginVerdict = 'no_mark' | 'cannot_assess' | 'excess' | 'breach';

export interface MarginAdequacy {
  verdict: MarginVerdict;
  /** mark × quantity (current exposure), Decimal string or null when no mark. */
  exposure: string | null;
  /** Posted collateral — collateral_value only; NO contract_value fallback (QA finding A). */
  collateral: string | null;
  /** Required collateral = contract_value (already margined at booking — `qty × gross_price ×
   *  margin_decimal`; get_required_collateral() returns it verbatim, never × margin again). */
  required: string | null;
  /** collateral − required; null when adequacy can't be honestly assessed. */
  delta: string | null;
  /** Absolute magnitude of delta (shortfall or excess amount) for display; null when delta null. */
  deltaAbs: string | null;
  /** Current mark − booking price (signed Decimal string); "" when no mark OR no booking price. */
  markDelta: string;
  /** lend-side (lend/repo) vs borrow-side (borrow/reverse_repo) — who posts collateral (M2). */
  isLendSide: boolean;
  /** Marked exposure strictly exceeds posted collateral while the requirement stays
   *  booking-based — the "Excess" verdict then masks a mark-to-market shortfall (M1 signal).
   *  Non-cash only: for cash collateral the requirement IS the marked value, so the verdict
   *  already reflects it and this stays false. */
  exposureExceedsCollateral: boolean;
  /** Cash-collateral GMSLA trade (LR-414 D): `collateral` is the cash posted at booking
   *  (contract_value) and `required` is the MARKED margined value the cash must cover
   *  (closing_cash). Lets the card relabel the two Fields and reflects the §4/§5 basis. */
  isCashCollateral: boolean;
}

type MarginInput = Pick<
  PositionDetail,
  | 'latest_mark_price'
  | 'collateral_value'
  | 'contract_value'
  | 'closing_cash'
  | 'collateral_type'
  | 'quantity'
  | 'gross_price'
  | 'trade_type'
>;

/**
 * Single source of the margin/collateral verdict for BOTH the Analytics card and
 * the page KPI bar. When there's no mark → 'no_mark' (do not fabricate).
 *
 * Two collateral bases, by `collateral_type`:
 *  - CASH (GMSLA lend/borrow — LR-414 D, canonical §4/§5 of
 *    docs/planning/LR-104/customer-decisions.md, customer-confirmed Jun 2026;
 *    mirrors trade-math.ts::getTradeExposure + the MtM page): posted collateral =
 *    the cash received at booking (contract_value, already margined); requirement =
 *    the MARKED margined value the cash must cover (closing_cash = last_marked_price
 *    × qty × margin_decimal for GMSLA). The requirement TRACKS the mark, so a price
 *    move that the fixed posted cash no longer covers IS the margin call (lend-side:
 *    counterparty tops up, due to us). Replaces the old blanket 'cannot_assess'.
 *  - NON-CASH (and default): posted securities (collateral_value, re-marked) vs the
 *    BOOKING requirement (contract_value, already margined — WR-04, mark-spike-immune;
 *    never contract_value × margin, which would double-apply the margin: par × margin²).
 *    'cannot_assess' when collateral_value is null (no false breach — QA A).
 */
export function deriveMarginAdequacy(trade: MarginInput): MarginAdequacy {
  // Collateral-risk direction (M2): lend-side (lend/repo) RECEIVES collateral — a
  // shortfall is the counterparty's margin call to us; borrow-side (borrow/reverse_repo)
  // POSTS collateral — a "shortfall" is our own top-up obligation, not counterparty risk.
  // Mirrors Position.pnl_direction's lend-side axis.
  const isLendSide = trade.trade_type === 'lend' || trade.trade_type === 'repo';

  // Cash collateral is a GMSLA (lend/borrow) concept here — repo/reverse margin is the
  // per-CP cash-pool of LR-385, computed on the MtM page, NOT this per-trade card. So a
  // repo with collateral_type='cash' stays on the booking-basis branch (and closing_cash
  // for GMRA is contract_value + interest, which would be the wrong requirement).
  const isCashCollateral =
    trade.collateral_type === 'cash' &&
    (trade.trade_type === 'lend' || trade.trade_type === 'borrow');

  // Gate on latest_mark_price (raw last mark, null when never marked) — NOT
  // last_marked_price, which the backend masks with the current_market_price
  // booking snapshot. A trade never through MTM must read "no mark" honestly
  // rather than show exposure/adequacy off a snapshot labelled "as of last mark".
  // latest_mark_price is the field LR-368 added to distinguish first-mark from never.
  const markPrice = toDecimalString(trade.latest_mark_price);
  if (markPrice == null) {
    return {
      verdict: 'no_mark',
      exposure: null,
      collateral: null,
      required: null,
      delta: null,
      deltaAbs: null,
      markDelta: '',
      isLendSide,
      exposureExceedsCollateral: false,
      isCashCollateral,
    };
  }

  const exposure = multiplyPriceByQuantity(markPrice, trade.quantity);
  // Posted collateral and its requirement split by collateral type (see the
  // function docstring). Cash: contract_value (posted cash) vs closing_cash (marked
  // margined requirement). LR-626 hid closing_cash from the Trade Terms tab for
  // GMSLA — this very branch — but the verdict still reads it, so it must keep
  // being served. Non-cash: collateral_value (marked securities, no fallback) vs
  // contract_value (booking, WR-04).
  const collateral = isCashCollateral
    ? toDecimalString(trade.contract_value)
    : toDecimalString(trade.collateral_value);
  const required = isCashCollateral
    ? toDecimalString(trade.closing_cash)
    : toDecimalString(trade.contract_value);

  const canAssess = collateral != null && required != null;
  const delta = canAssess ? subtractDecimalStrings(collateral, required) : null;
  const breach = delta != null && (toDecimalString(delta)?.startsWith('-') ?? false);
  const verdict: MarginVerdict = !canAssess ? 'cannot_assess' : breach ? 'breach' : 'excess';
  const deltaAbs = delta == null ? null : breach ? negateDecimalString(delta) : delta;

  // M1 honest signal (NON-CASH only): marked exposure strictly above posted collateral.
  // The non-cash requirement stays booking-based (mark-spike-immune, WR-04), so an
  // "Excess" badge would hide that the loaned value already tops the collateral. For
  // cash the requirement already tracks the mark, so the verdict reflects it directly
  // and this stays false (no redundant amber note next to a real breach). Pure Decimal.
  const exposureMinusCollateral =
    !isCashCollateral && exposure != null && collateral != null
      ? subtractDecimalStrings(exposure, collateral)
      : null;
  const exposureExceedsCollateral =
    exposureMinusCollateral != null &&
    !exposureMinusCollateral.startsWith('-') &&
    !/^[+-]?0*(\.0*)?$/.test(exposureMinusCollateral);

  // Price-movement signal: mark vs the BOOKING price (gross_price / COB T-1), not
  // margin_price (= gross × margin_decimal), which would bake the margin premium
  // into the delta (≈ −2% at 102% margin even on a flat market). No booking price
  // (gross_price absent) → no delta ("") rather than a mislabelled mark = delta (L7).
  const markDelta =
    toDecimalString(trade.gross_price) == null
      ? ''
      : subtractDecimalStrings(markPrice, trade.gross_price);

  return {
    verdict,
    exposure,
    collateral,
    required,
    delta,
    deltaAbs,
    markDelta,
    isLendSide,
    exposureExceedsCollateral,
    isCashCollateral,
  };
}
